import React, { useEffect, useMemo, useRef } from 'react';
import { FormikProps } from 'formik';
import { Flex, Box, Label, Container } from 'theme-ui';
import { GrayFormikField } from '@/components/GrayFormikField';
import { ProductType, DiscountType, DiscountLengthUnit } from '@/types/types';
import CustomDatePicker from '@/components/CustomDatePicker';
import {
    useGetChargePlansQuery,
    useGetVossPlansQuery,
    useGetVossProductGroupsQuery,
    useGetVossProductsQuery,
    useGetVossTierQuery,
    useLazyGetVossProductsQuery,
} from '@/store/apiService';
import Button from '@/components/Button';
import Error from '@/components/Error';
import FieldGroup from '@/components/FieldGroup';
import { FormValues } from './CreateDiscountAgreement';
import { flattenObject } from '@/utils/helpers';

interface FormProps extends FormikProps<FormValues> {
    additionalErrors?: React.ReactNode;
}

const discountTypeOptions = [
    {
        value: DiscountType[DiscountType.FlatPrice],
        label: 'Flat price',
    },
    {
        value: DiscountType[DiscountType.Percentage],
        label: 'Percentage',
    },
];

const discountPeriodUnitOptions = [
    {
        value: DiscountLengthUnit[DiscountLengthUnit.Day],
        label: 'Day',
    },
    {
        value: DiscountLengthUnit[DiscountLengthUnit.Month],
        label: 'Month',
    },
    {
        value: DiscountLengthUnit[DiscountLengthUnit.Year],
        label: 'Year',
    },
];

const bracketsContentRegex = / \((.*?)\)/g;

export const DiscountAgreementForm: React.FC<FormProps> = ({
    handleChange,
    additionalErrors,
    ...props
}) => {
    const chargePlans = useGetChargePlansQuery({});
    const vossProductsState = useGetVossProductsQuery({});
    const tiers = useGetVossTierQuery({});
    const plans = useGetVossPlansQuery({});


    const chargePlanOptions = useMemo(() => {
        return chargePlans.data && tiers.data
            ? chargePlans.data.map((chargePlan) => {
                const tier = tiers.data?.find((t) => t.id === chargePlan.tierId);
                const plan = plans.data?.find((t) => t.Id === chargePlan.planId);

                const label =
                    tier && plan
                        ? `${tier?.name} ${plan.Name} (${chargePlan.id.substr(-6)})`
                        : chargePlan.id;

                return {
                    value: chargePlan.id,
                    label,
                };
            })
            : [];
    }, [chargePlans.data, tiers.data, plans.data]);

    const productGroupOptions = useMemo(() => {
        return chargePlans?.data
            ? chargePlans.data
                .find((cp) => cp.id === props.values?.ChargePlanDiscounts?.ChargePlanId)
                ?.productGroupPricings?.map((el) => ({
                    value: el.productGroupId,
                    label: el.articleNumber,
                }))
            : [];
    }, [chargePlans.data, props.values?.ChargePlanDiscounts?.ChargePlanId]);

    const productOptions = useMemo(() => {
        return chargePlans?.data
            ? chargePlans.data
                ?.find((cp) => cp.id === props.values?.ChargePlanDiscounts?.ChargePlanId)
                ?.productGroupPricings?.find(
                    (productGroupPricing) =>
                        productGroupPricing.productGroupId ===
                        props.values.ChargePlanDiscounts?.ProductGroupDiscounts?.ProductGroupId
                )
                ?.productPricings?.map((el) => {
                    const product = vossProductsState.data?.find((t) => t.Id === el.productId);
                    const label = product
                        ? `${product.Name} (${el.productId.substr(-6)})`
                        : el.productId;

                    return {
                        value: el.productId,
                        label,
                    };
                })
            : [];
    }, [
        chargePlans.data,
        vossProductsState.data,
        props.values?.ChargePlanDiscounts?.ChargePlanId,
        props.values.ChargePlanDiscounts?.ProductGroupDiscounts?.ProductGroupId,
    ]);

    const generatedDescription = useRef('');

    // Generate description effect
    useEffect(() => {
        if (!props.dirty) return;
        const currentDescription = props.values.Description ?? '';
        if (currentDescription !== '' && currentDescription !== generatedDescription.current) return;

        let description = [];
        let discountLine = { unit: '', value: '' };
        let chargePlanLine = { unit: '', value: '' }
        let productLine = { unit: '', value: '' };
        let discountPeriodLine = { unit: '', value: '' }

        const discountPeriodValue = props.values.DiscountPeriod?.Length?.Value;
        const discountPerioudUnit = discountPeriodUnitOptions?.find(
            (o) => o.value.toString() === props.values.DiscountPeriod?.Length?.Unit
        );
        const discountType = discountTypeOptions.find(
            (o) =>
                o.value.toString() ===
                props.values.ChargePlanDiscounts?.ProductGroupDiscounts?.ProductPricingDiscounts
                    ?.DiscountType
        );
        const discountValue = props.values.ChargePlanDiscounts?.ProductGroupDiscounts?.ProductPricingDiscounts?.UnitPriceDiscount;

        Object.entries(flattenObject(props.values)).forEach(entry => {
            if (entry[0].match(/UnitPriceDiscount/)) {
                discountLine.value = discountValue?.toString();
            } else if (entry[0].match(/DiscountType/)) {
                discountLine.unit = discountType?.label ?? '';
            } else if (entry[0].match(/ProductId/)) {
                productLine.value = productOptions
                    ?.find(
                        (p) =>
                            p.value ===
                            props.values.ChargePlanDiscounts.ProductGroupDiscounts
                                .ProductPricingDiscounts?.ProductId
                    )
                    ?.label
                    ?.replace(bracketsContentRegex, '')
            } else if (entry[0].match(/DiscountPeriod.Length.Unit/)) {
                discountPeriodLine.unit = discountPerioudUnit?.label ?? '';
            } else if (entry[0].match(/DiscountPeriod.Length.Value/)) {
                discountPeriodLine.value = discountPeriodValue?.toString();
            } else if (entry[0].match(/ChargePlanId/)) {
                chargePlanLine.value = chargePlanOptions
                    ?.find((c) => c.value === props.values?.ChargePlanDiscounts?.ChargePlanId)
                    ?.label
                    ?.replace(bracketsContentRegex, '');
            }
        });

        if (chargePlanLine.value) {
            description.push(`Charge plan: ${chargePlanLine.value}`)
        }

        if (productLine.value) {
            description.push(`Product: ${productLine.value}`)
        }

        if (discountLine.unit && discountLine.value) {
            description.push(`Discount: ${discountLine.value} ${discountLine.unit}`)
        }

        if (discountPeriodLine.unit && discountPeriodLine.value) {
            description.push(`Discount period: ${discountPeriodLine.value} ${discountPeriodLine.unit}`)
        }

        generatedDescription.current = description.join('\n');
        props.setFieldValue('Description', generatedDescription.current);
    }, [
        props.values.DiscountPeriod?.Length?.Unit,
        props.values.DiscountPeriod?.Length?.Value,
        props.values.ChargePlanDiscounts?.ProductGroupDiscounts?.ProductPricingDiscounts
            ?.DiscountType,
        props.values.ChargePlanDiscounts?.ProductGroupDiscounts?.ProductPricingDiscounts?.ProductId,
        props.values.ChargePlanDiscounts?.ProductGroupDiscounts?.ProductPricingDiscounts
            ?.UnitPriceDiscount,
        props.values?.ChargePlanDiscounts?.ChargePlanId,
        props.values?.ChargePlanDiscounts?.ProductGroupDiscounts?.ProductGroupId,
        props.values?.ChargePlanDiscounts?.ProductGroupDiscounts?.ProductPricingDiscounts
            ?.ProductId,
        props.dirty,
        productOptions,
        chargePlanOptions,
    ]);


    return (
        <>
            <GrayFormikField name="Name" type="text" label="Name" />
            <GrayFormikField name="Description" type="textarea" label="Description" />

            <GrayFormikField
                label="Valid From"
                type="datetime"
                variation="normal"
                field={{
                    name: 'ValidFrom',
                    value: !props.values.ValidFrom ? null : new Date(props.values.ValidFrom),
                }}
                onChange={(value) => {
                    props.setFieldValue('ValidFrom', value.toISOString());
                }}
            />

            <FieldGroup inline={true}>
                <GrayFormikField
                    name="DiscountPeriod.Length.Value"
                    type="number"
                    label="Discount period value"
                />
                <GrayFormikField
                    label="Discount period unit"
                    type="select"
                    name="DiscountPeriod.Length.Unit"
                    placeholder="Select discount period unit"
                    options={discountPeriodUnitOptions}
                />
            </FieldGroup>

            <GrayFormikField
                label="Charge plan"
                type="select"
                name="ChargePlanDiscounts.ChargePlanId"
                placeholder="Select charge plan"
                options={chargePlanOptions}
                disabled={chargePlans.isFetching || tiers.isFetching || plans.isFetching}
                isLoading={chargePlans.isFetching || tiers.isFetching || plans.isFetching}
                onChange={(evt) => {
                    props.setFieldValue('ChargePlanDiscounts.ProductGroupDiscounts.ProductGroupId', '');
                    props.setFieldValue(
                        'ChargePlanDiscounts.ProductGroupDiscounts.ProductPricingDiscounts.ProductId',
                        ''
                    )
                    props.setFieldValue('ChargePlanDiscounts.ChargePlanId', evt.target.value);
                }
                }
            />

            <GrayFormikField
                label="Product group"
                type="select"
                name="ChargePlanDiscounts.ProductGroupDiscounts.ProductGroupId"
                placeholder="Select product group"
                options={productGroupOptions}
                disabled={!props.values?.ChargePlanDiscounts?.ChargePlanId}
                onChange={(evt) => {
                    props.setFieldValue(
                        'ChargePlanDiscounts.ProductGroupDiscounts.ProductPricingDiscounts.ProductId',
                        ''
                    );
                    // NOTE: why we need to set this?
                    props.setFieldValue('ChargePlanDiscounts.ProductGroupDiscounts.ProductGroupId', evt.target.value);
                }
                }
            />

            <GrayFormikField
                label="Product"
                type="select"
                name="ChargePlanDiscounts.ProductGroupDiscounts.ProductPricingDiscounts.ProductId"
                placeholder="Select a product"
                options={productOptions}
                disabled={!props.values?.ChargePlanDiscounts?.ProductGroupDiscounts?.ProductGroupId}
            />

            <FieldGroup inline={true}>
                <GrayFormikField
                    label="Discount type"
                    type="select"
                    name="ChargePlanDiscounts.ProductGroupDiscounts.ProductPricingDiscounts.DiscountType"
                    placeholder="Select discount type"
                    options={discountTypeOptions}
                />
                <GrayFormikField
                    name="ChargePlanDiscounts.ProductGroupDiscounts.ProductPricingDiscounts.UnitPriceDiscount"
                    type="number"
                    label="Discount value"
                />
            </FieldGroup>

            {additionalErrors ? (
                <Box>
                    <Container variant="error-message">
                        <Error error={additionalErrors as any} />
                    </Container>
                </Box>
            ) : null}
            <Flex
                p="2"
                sx={{
                    width: '100%',
                    gap: 2,
                    justifyContent: 'flex-end',
                }}
            >
                <Button
                    isLoading={props.isSubmitting}
                    disabled={props.isSubmitting}
                    type="submit"
                    onClick={props.submitForm}
                >
                    Save
                </Button>
                {/* TODO: add errors */}
            </Flex>
        </>
    );
};
